import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatApiErrorDetail } from "@/lib/api";
import { FormField, adminInput } from "./shared";

const BLANK = { name: "", email: "", company: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const ClientDialog = ({ open, onClose, onSave }) => {
  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setForm(BLANK); setErrors({}); } }, [open]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!EMAIL_RE.test(form.email.trim())) errs.email = "Please enter a valid email";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await onSave({ name: form.name.trim(), email: form.email.trim(), company: form.company.trim() });
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409) setErrors({ email: "A user with this email already exists" });
      else if (err.response?.status === 422) setErrors({ email: "Please enter a valid email" });
      else setErrors({ form: formatApiErrorDetail(d) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="client-dialog" className="max-w-md border-white/10 bg-[#0D111A]">
        <DialogHeader><DialogTitle className="text-white">Add client</DialogTitle></DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          <FormField id="client-name" label="Name" error={errors.name}>
            <input id="client-name" data-testid="client-input-name" value={form.name} onChange={set("name")} className={adminInput(errors.name)} />
          </FormField>
          <FormField id="client-email" label="Email" error={errors.email}>
            <input id="client-email" type="email" data-testid="client-input-email" value={form.email} onChange={set("email")} className={adminInput(errors.email)} />
          </FormField>
          <FormField id="client-company" label="Company">
            <input id="client-company" data-testid="client-input-company" value={form.company} onChange={set("company")} className={adminInput(false)} />
          </FormField>
          {errors.form && <p className="text-sm text-red-400">{errors.form}</p>}
          <p className="text-xs text-slate-500">We'll email a portal invite with a set-password link (valid 7 days, single use).</p>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} data-testid="client-cancel-button" className="rounded-full border border-white/10 px-5 py-2 text-sm text-slate-300 hover:bg-white/5">Cancel</button>
            <button type="submit" disabled={busy} data-testid="client-save-button" className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2 text-sm font-semibold text-[#07090E] hover:bg-cyan-300 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Add & send invite
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
